// src/components/Documents/ManageCategories.js

import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

export default function ManageCategories({ open, onClose, chatbotId, onCategoriesUpdate }) {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // State for creating a new category
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // State for editing an existing category
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchCategories = async () => {
    if (!chatbotId) return;
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const params = { chatbot_id: chatbotId };
      const response = await axiosInstance.get('/api/categories', { params });
      setCategories(response.data.data);
    } catch (err) {
      setError("Failed to fetch categories. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open, chatbotId]);

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setError("Category name cannot be empty.");
      return;
    }
    setIsCreating(true);
    setError("");
    setSuccess("");
    try {
      const params = { chatbot_id: chatbotId };
      const payload = { name: newCategoryName.trim() };
      const response = await axiosInstance.post('/api/categories', payload, { params });
      
      setSuccess(response.data.message);
      setNewCategoryName(""); // Clear input
      await fetchCategories(); // Refetch the list
      onCategoriesUpdate(); // Notify parent component
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create category.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"? Documents in this category will be moved to "Uncategorized".`)) {
      return;
    }
    setError("");
    setSuccess("");
    try {
      const params = { chatbot_id: chatbotId };
      const response = await axiosInstance.delete(`/api/categories/${categoryId}`, { params });
      setSuccess(response.data.message);
      await fetchCategories(); // Refetch
      onCategoriesUpdate(); // Notify parent
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete category.");
    }
  };

  const handleStartEdit = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setError("");
    setSuccess("");
  };
  
  const handleCancelEdit = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
  };
  
  const handleUpdateCategory = async () => {
    if (!editingCategoryName.trim()) {
      setError("Category name cannot be empty.");
      return;
    }
    setIsUpdating(true);
    setError("");
    setSuccess("");
    try {
      const params = { chatbot_id: chatbotId };
      const payload = { name: editingCategoryName.trim() };
      const response = await axiosInstance.put(`/api/categories/${editingCategoryId}`, payload, { params });

      setSuccess(response.data.message);
      handleCancelEdit(); // Exit editing mode
      await fetchCategories(); // Refetch
      onCategoriesUpdate(); // Notify parent
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update category.");
    } finally {
      setIsUpdating(false);
    }
  };


  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 500 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
        p: 3,
      }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Manage Categories
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

        {/* --- Create New Category Form --- */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="New Category Name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            disabled={isCreating}
          />
          <Button
            variant="contained"
            onClick={handleCreateCategory}
            disabled={isCreating}
            startIcon={isCreating ? <CircularProgress size={20} /> : <AddCircleOutlineIcon />}
          >
            Add
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />

        {/* --- List of Existing Categories --- */}
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {categories.map((cat) => (
                <ListItem key={cat.id} secondaryAction={
                  editingCategoryId === cat.id ? (
                    <Stack direction="row" spacing={1}>
                      <IconButton edge="end" color="primary" onClick={handleUpdateCategory} disabled={isUpdating}>
                        {isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
                      </IconButton>
                      <IconButton edge="end" onClick={handleCancelEdit} disabled={isUpdating}>
                        <CancelIcon />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <IconButton edge="end" onClick={() => handleStartEdit(cat)} disabled={cat.name === 'Uncategorized'}>
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" color="error" onClick={() => handleDeleteCategory(cat.id, cat.name)} disabled={cat.name === 'Uncategorized'}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  )
                }>
                  {editingCategoryId === cat.id ? (
                    <TextField
                      variant="standard"
                      fullWidth
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <ListItemText primary={cat.name} />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Modal>
  );
}